/**
 * StaticMapPreview - Renders a static map image using Mapbox Static Images API.
 * Used in LocationView to show a preview of the selected location.
 */

import { usePublicKeys } from "@/components/providers/PublicKeysProvider";

interface StaticMapPreviewProps {
  lat: number;
  lon: number;
  zoom?: number; // Default: 14, city-level: 12
  width?: number; // Default: 400 (responsive)
  height?: number; // Default: 120
  className?: string;
  markerColor?: string; // Hex without #, default: 2A49AD (mobile's default)
}

/**
 * Renders a static map preview using Mapbox Static Images API.
 * Uses @2x for retina displays and shows a pin marker at the specified location.
 */
export function StaticMapPreview({
  lat,
  lon,
  zoom = 14,
  width = 400,
  height = 120,
  className = "",
  markerColor = "2A49AD",
}: StaticMapPreviewProps) {
  const { keys } = usePublicKeys();
  const mapboxKey = keys?.mapbox_public_key;

  if (!mapboxKey) {
    return (
      <div
        className={`bg-surface-1 flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <span className="text-body-2 text-text-tertiary">Loading map...</span>
      </div>
    );
  }

  // Mapbox Static Images API URL format:
  // https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/
  // pin-s+{color}({lon},{lat})/{lon},{lat},{zoom},0/{width}x{height}@2x
  // ?access_token={mapboxKey}
  const staticMapUrl = `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-s+${markerColor}(${lon},${lat})/${lon},${lat},${zoom},0/${width}x${height}@2x?access_token=${mapboxKey}`;

  return (
    <img
      src={staticMapUrl}
      alt="Map preview"
      className={`w-full object-cover ${className}`}
      style={{ height }}
      loading="lazy"
    />
  );
}
